import {
    Checkbox,
    LexicalEditor,
    LexicalEditorState,
    LexicalHandle,
    LexicalSerializedEditorState,
} from '@missionlabs/smartagent-app-components'
import type { Attachment } from '@missionlabs/smartagent-service-thread'
import useChat from 'hooks/redux/useChat'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { getQuickReplies } from 'store/chat/chat.actions'
import SendButtonComponent from 'views/Tasks/Task/Thread/EmailRedesigned/ToolbarButtons/SendButton'
import UploadAttachmentsComponent from 'views/Tasks/Task/Thread/EmailRedesigned/UploadMultipleAttachments'

export const EMPTY_SERIALIZED_STATE = {} as LexicalSerializedEditorState

export interface Props {
    lexicalEditorRef: React.RefObject<LexicalHandle>
    editorState: LexicalSerializedEditorState
    docId?: string
    label?: string
    onEditorStateChange: (state: LexicalEditorState) => void
    isAttachmentLoading: boolean
    ccReplyChecked?: boolean
    isExternalParticipantSelected?: boolean
    setCCReplyChecked?: (value: boolean) => void
    isSendingMessage: boolean
    isSendButtonDisabled: boolean
    isCCReplyHidden?: boolean
    isUploadAttachmentsHidden?: boolean
    isSendButtonHidden?: boolean
    threadChannel: string
    onAttachStart?: (files: File[]) => void
    onAttachEnd?: (attachments: Attachment[]) => void
    onError?: (message: string) => void
    SendButton?: React.ReactNode
    UploadAttachmentsButton?: React.ReactNode
}

const EditorRedesigned = ({
    lexicalEditorRef,
    editorState,
    docId,
    label,
    onEditorStateChange,
    isAttachmentLoading,
    ccReplyChecked,
    setCCReplyChecked = () => {},
    isExternalParticipantSelected,
    isSendingMessage,
    isSendButtonDisabled,
    isCCReplyHidden,
    isUploadAttachmentsHidden,
    isSendButtonHidden,
    threadChannel,
    onAttachStart = () => {},
    onAttachEnd = () => {},
    onError = () => {},
    ...props
}: Props) => {
    const SendButton = props.SendButton ?? (
        <SendButtonComponent
            loading={isSendingMessage}
            disabled={isSendButtonDisabled}
            buttonClassName="lexicalEditor-sendBtn"
        />
    )

    const UploadAttachmentsButton = props.UploadAttachmentsButton ?? (
        <UploadAttachmentsComponent
            channel={threadChannel}
            isLoading={isAttachmentLoading}
            onAttachStart={onAttachStart}
            onAttachEnd={onAttachEnd}
            onError={onError}
            isEditorRedesigned
        />
    )

    const CcReplyButton = (
        <div className="lexicalEditor-ccReplyButton">
            <Checkbox
                square
                checked={ccReplyChecked && !isExternalParticipantSelected}
                disabled={isExternalParticipantSelected}
                label="Include Cc in the reply"
                onChange={setCCReplyChecked}
            />
        </div>
    )

    const { quickReplies, quickRepliesLoading } = useChat() || {}
    const dispatch = useDispatch()

    useEffect(() => {
        if (!quickRepliesLoading) {
            dispatch(getQuickReplies(threadChannel))
        }
    }, [threadChannel, quickRepliesLoading])

    return (
        <>
            {label && (
                <div className="sa-editor-label">
                    <label>{label}</label>
                </div>
            )}
            <LexicalEditor
                ref={lexicalEditorRef}
                editorState={editorState}
                onEditorStateChange={onEditorStateChange}
                placeholder="Enter message..."
                docId={docId}
                UploadAttachmentsButton={isUploadAttachmentsHidden ? null : UploadAttachmentsButton}
                SendButton={isSendButtonHidden ? null : SendButton}
                CcReplyButton={isCCReplyHidden ? null : CcReplyButton}
                quickReplies={quickReplies?.[threadChannel] || []}
            />
        </>
    )
}

export default EditorRedesigned
